import * as http from 'utils/http';
import { getError } from 'utils/http';
import { delay } from 'redux-saga';
import { all, call, cancel, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { sendEventToSegment, TrackeableEvent } from 'redux/modules/analytics';

import {
  CREATE_LOG_END,
  CREATE_LOG_START,
  CREATE_MANUAL_LOG,
  EDIT_LOG_END,
  EDIT_LOG_START,
} from 'redux/modules/analytics/events';

import {
  createJourneyLogRequest,
  createShipperOrderLogRequest,
  getEndpoint,
  getJourneyLogRequest,
  getShipperOrderLogRequest,
  getItineraryLogRequest,
  resolveJourneyLogRequest,
  resolverShipperOrderLogRequest,
  updateJourneyLogRequest,
  updateShipperOrderLogRequest,
} from './endpoints';

import {
  clearState,
  setAppendLogs,
  setCreatingLogs,
  setEditableAfterCreation,
  setLoading,
  setLoadingNextPage,
  setLogs,
  setManualLogOpened,
  setNextLogsPage,
  setOpenedCancellationLog,
  setPatchLog,
  setPrependLogs,
  setPutLog,
  setRemoveEditableAfterCreation,
  setTotalLogs,
} from './duck';

import { getNextLogsPageLink } from './selectors';

const openCancellationLog$ = 'logs/openCancellationLog$';
const createCancellationLog$ = 'logs/createCancellationLog$';

const clearLogs$ = 'logs/clearLogs$';
const fetchLogs$ = 'logs/fetchLogs$';
const editLog$ = 'logs/editLog$';
const resolveLog$ = 'logs/resolveLog$';
const createJourneyLog$ = 'logs/createJourneyLog$';
const createShipperOrderLog$ = 'logs/createShipperOrderLog$';
const openManualLog$ = 'logs/openManualLog$';
const createManualLog$ = 'logs/createManualLog$';
const openLogEdition$ = 'logs/openLogEdition$';
const closeLogEdition$ = 'logs/closeLogEdition$';
const fetchNextLogsPage$ = 'logs/fetchNextLogsPage';

const openCancellationLog = (payload) => ({
  type: openCancellationLog$,
  payload,
});

const createCancellationLog = (payload) => ({
  type: createCancellationLog$,
  payload,
});

const clearLogs = () => ({
  type: clearLogs$,
  payload: {},
});

const fetchLogs = ({ itineraryId, journeyId, shipperOrderId, hideSomeLogs }) => ({
  type: fetchLogs$,
  payload: {
    itineraryId,
    journeyId,
    shipperOrderId,
    hideSomeLogs,
  },
});

const fetchNextLogsPage = ({ hideSomeLogs }) => {
  return {
    type: fetchNextLogsPage$,
    payload: { hideSomeLogs },
  };
};

const editLog = (log) => ({
  type: editLog$,
  payload: log,
});

const resolveLog = (log) => ({
  type: resolveLog$,
  payload: log,
});

const openLogEdition = (id) => ({
  type: openLogEdition$,
  payload: {
    id,
  },
});

const closeLogEdition = (id) => ({
  type: closeLogEdition$,
  payload: {
    id,
  },
});

const createJourneyLog = ({ journeyId, action, editableAfterCreation = false, changes }) => ({
  type: createJourneyLog$,
  payload: {
    journeyId,
    action,
    editableAfterCreation,
    changes,
  },
});

const createShipperOrderLog = ({
  shipperOrderId,
  action,
  editableAfterCreation = false,
  changes,
}) => ({
  type: createShipperOrderLog$,
  payload: {
    shipperOrderId,
    action,
    editableAfterCreation,
    changes,
  },
});

const createManualLog = (payload) => ({
  type: createManualLog$,
  payload,
});

const openManualLog = () => ({
  type: openManualLog$,
  payload: {
    opened: true,
  },
});

const closeManualLog = () => ({
  type: openManualLog$,
  payload: {
    opened: false,
  },
});

let pollingTask;
const patchIfHasValue = (name, value) => (!!value ? { [name]: value } : {});
const addManualLogTrackeableEvent = new TrackeableEvent();
const editEvents = {
  inlineEdit: EDIT_LOG_END,
  extendedCreation: CREATE_LOG_END,
};

const hiddenLogs = [
  'TASK_REVIEWED',
  'TASK_TO_PENDING_REVIEW',
  'TASK_SOLVED_AUTOMATICALLY',
  'TASK_SOLVED_MANUALLY',
  'TASK_CREATED_AUTOMATICALLY',
  'TASK_ASSIGNED_TO_NEW_OWNER',
  'CHANGE_JOURNEY_STATUS',
  'CHAT_TASK_SOLVED',
  'CHAT_TASK_CREATED',
];

const filterLogs = (logs) => {
  if (!Array.isArray(logs)) return [];

  return logs.filter((log) => {
    const { action } = log || {};

    if (typeof action !== 'string') return true;

    return !hiddenLogs.includes(action);
  });
};

function* handleClearLogs() {
  if (pollingTask) yield cancel(pollingTask);
  yield put(clearState());
}

export function* handleFetchLogs({ payload }) {
  const { itineraryId, journeyId, shipperOrderId, hideSomeLogs } = payload;

  const endpointFn = getEndpoint({ itineraryId, journeyId, shipperOrderId })(
    getJourneyLogRequest,
    getShipperOrderLogRequest,
    getItineraryLogRequest
  );

  if (!endpointFn) {
    return;
  }

  try {
    yield put(setLoading(true));
    const logs = yield call(endpointFn, { page_size: 50 });
    let logsFiltered = hideSomeLogs ? yield call(filterLogs, logs.results) : logs.results;
    yield put(setLogs(logsFiltered));
    yield put(setTotalLogs(logs.count));
    yield put(setNextLogsPage(logs.next));
    yield put(setLoading(false));

    pollingTask = yield fork(pollingLogsTask, {
      itineraryId,
      journeyId,
      shipperOrderId,
      hideSomeLogs,
    });
  } catch (err) {
    console.error('Error getting the logs', err);
  }
}

export function* handleFetchNextLogsPage({ hideSomeLogs }) {
  if (pollingTask) yield cancel(pollingTask);

  yield put(setLoadingNextPage(true));
  try {
    const nextLogsPageLink = yield select(getNextLogsPageLink);

    if (!nextLogsPageLink) return;

    const newPageOfLogs = yield call(http.get, nextLogsPageLink);

    const logsFiltered = hideSomeLogs
      ? yield call(filterLogs, newPageOfLogs?.results)
      : newPageOfLogs?.results;

    yield put(setAppendLogs(logsFiltered));
    yield put(setNextLogsPage(newPageOfLogs.next));
  } catch (e) {
    console.error('Error fetching next page logs: ', e);
  } finally {
    yield put(setLoadingNextPage(false));
  }
}

function* pollingLogsTask({ itineraryId, journeyId, shipperOrderId, hideSomeLogs }) {
  try {
    while (true) {
      yield call(delay, 50000);

      const endpointFn = getEndpoint({ itineraryId, journeyId, shipperOrderId })(
        getJourneyLogRequest,
        getShipperOrderLogRequest,
        getItineraryLogRequest
      );

      if (!endpointFn) {
        return;
      }

      const { results } = yield call(endpointFn, { page_size: 50 });

      const logs = hideSomeLogs ? yield call(filterLogs, results) : results;

      yield put(setLogs(logs));
      yield put(setTotalLogs(results?.length));
    }
  } catch (e) {
    console.error(e);
  }
}

/*
 * CRUD action handlers to logs, interacting with the api
 */
function* handleCreateJourneyLog({ payload }) {
  const { action, journeyId, editableAfterCreation, changes } = payload;
  try {
    const log = yield call(createJourneyLogRequest, journeyId, {
      action,
      log_type: 'INFO',
      comment: undefined,
      reason: undefined,
      source_id: undefined,
      source_type: undefined,
      changes,
    });
    yield put(setPrependLogs([log]));

    if (editableAfterCreation) {
      yield put(setEditableAfterCreation({ logId: log.id }));

      yield call(sendEventToSegment, CREATE_LOG_START, {
        id: log.id,
        action: log.action,
        log: log.log_type,
      });
    }
  } catch (error) {
    console.error('Error creating log', error);
  }
}

function* handleCreateShipperOrderLog({ payload }) {
  const { action, shipperOrderId, editableAfterCreation, changes } = payload;
  try {
    const log = yield call(createShipperOrderLogRequest, shipperOrderId, {
      action,
      log_type: undefined,
      comment: undefined,
      reason: undefined,
      source_id: undefined,
      source_type: undefined,
      changes,
    });

    yield put(setPrependLogs([log]));

    if (editableAfterCreation) {
      yield put(setEditableAfterCreation({ logId: log.id }));

      yield call(sendEventToSegment, CREATE_LOG_START, {
        id: log.id,
        action: log.action,
        log: log.log_type,
      });
    }
  } catch (error) {
    console.error('Error creating log', error);
  }
}

function* handleCreateManualLog({ payload }) {
  const { action, comment, log_type, reason, shipperOrders, journeyId } = payload;

  try {
    const creatingLogs =
      shipperOrders?.length > 0
        ? shipperOrders.map((shipperOrderId) => ({
            action,
            comment,
            log_type,
            reason,
            shipper_order_id: shipperOrderId,
            UIIsCreating: true,
            journey_id: journeyId,
          }))
        : [
            // Just a journey log (without shipper orders selected)
            {
              action,
              comment,
              log_type,
              reason,
              UIIsCreating: true,
              journey_id: journeyId,
            },
          ];

    yield call(addManualLogTrackeableEvent.end, CREATE_MANUAL_LOG, {
      action,
      log_type,
    });

    yield put(setManualLogOpened(false));
    yield put(setCreatingLogs(creatingLogs));

    const newLogs = yield all(
      creatingLogs.map(({ shipper_order_id, ...log }) =>
        shipper_order_id
          ? call(createShipperOrderLogRequest, shipper_order_id, log)
          : call(createJourneyLogRequest, journeyId, log)
      )
    );

    yield put(setCreatingLogs([]));
    yield put(setPrependLogs(newLogs));
  } catch (error) {
    console.error('Error creating log', error);
  }
}

function* handleResolveLog({ payload }) {
  const { journeyId, shipperOrderId, id } = payload;

  const endpointFn = getEndpoint({ journeyId, shipperOrderId })(
    resolveJourneyLogRequest,
    resolverShipperOrderLogRequest
  );

  if (!endpointFn) {
    return;
  }

  yield put(setPatchLog({ id, UIIsEditing: true }));

  try {
    const log = yield call(endpointFn, id);
    yield put(setPutLog({ ...log, UIIsEditing: false }));
  } catch (error) {
    console.log('Error resolving log', getError(error));
  }
}

function* handleCreateCancellationLog({ payload }) {
  const { shipperOrderId, action, reason, comment } = payload;

  yield put(setOpenedCancellationLog(false));

  const optimisticLog = {
    action,
    reason,
    comment,
    log_type: 'INFO',
    shipper_order_id: shipperOrderId,
    UIIsCreating: true,
  };

  yield put(setCreatingLogs([optimisticLog]));

  const log = yield call(createShipperOrderLogRequest, shipperOrderId, optimisticLog);

  yield put(setCreatingLogs([]));
  yield put(setPrependLogs([log]));
}

function* handleEditLog({ payload }) {
  const { journeyId, shipperOrderId, id, reason, log_type, comment, type: eventType } = payload;

  const endpointFn = getEndpoint({ journeyId, shipperOrderId })(
    updateJourneyLogRequest,
    updateShipperOrderLogRequest
  );

  if (!endpointFn) {
    return;
  }

  const newLog = {
    ...patchIfHasValue('reason', reason),
    ...patchIfHasValue('log_type', log_type),
    ...patchIfHasValue('comment', comment),
  };

  yield put(setRemoveEditableAfterCreation({ logId: id }));
  yield put(setPatchLog({ id, ...newLog, UIIsEditing: true }));

  try {
    const log = yield call(endpointFn, id, newLog);

    yield put(setPutLog({ ...log, UIIsEditing: false }));

    if (editEvents[eventType]) {
      const { action, log_type } = log;

      yield call(sendEventToSegment, editEvents[eventType], {
        logId: id,
        action,
        log_type,
      });
    }
  } catch (error) {
    console.log('Error editing log', getError(error));
  }
}

/*
 * Action Handlers for VIEWS that allow to trigger CRUD actions for logs
 */
function* handleOpenLogEdition({ payload }) {
  const { id, action, log_type } = payload;

  try {
    yield call(sendEventToSegment, EDIT_LOG_START, {
      logId: id,
      action,
      log_type,
    });
  } catch (e) {
    console.error('Error sending event', e);
  }

  yield put(setPatchLog({ id, UIEditMode: 'normal' }));
}

function* handleCloseLogEdition({ payload }) {
  const { id } = payload;
  yield put(setPatchLog({ id, UIEditMode: false }));
}

function* handleOpenCancellationLog({ payload }) {
  const { action, shipperOrderId } = payload;
  yield put(setOpenedCancellationLog({ shipperOrderId, action }));
}

function* handleOpenManualLog({ payload }) {
  const { opened } = payload;

  yield put(setManualLogOpened(opened));

  try {
    yield call(addManualLogTrackeableEvent.start, CREATE_MANUAL_LOG);
  } catch (e) {
    console.error('error when sending segment event: ', e);
  }
}

// Watchers
export function* watchLog() {
  yield takeLatest(fetchLogs$, handleFetchLogs);
  yield takeEvery(clearLogs$, handleClearLogs);
  yield takeEvery(fetchNextLogsPage$, handleFetchNextLogsPage);

  yield takeEvery(createManualLog$, handleCreateManualLog);
  yield takeEvery(createJourneyLog$, handleCreateJourneyLog);
  yield takeEvery(createShipperOrderLog$, handleCreateShipperOrderLog);
  yield takeEvery(createCancellationLog$, handleCreateCancellationLog);
  yield takeEvery(editLog$, handleEditLog);
  yield takeEvery(resolveLog$, handleResolveLog);

  yield takeEvery(openLogEdition$, handleOpenLogEdition);
  yield takeEvery(closeLogEdition$, handleCloseLogEdition);
  yield takeEvery(openManualLog$, handleOpenManualLog);
  yield takeEvery(openCancellationLog$, handleOpenCancellationLog);
}

export {
  openCancellationLog,
  createCancellationLog,
  clearLogs,
  fetchLogs,
  fetchNextLogsPage,
  editLog,
  resolveLog,
  createJourneyLog,
  createShipperOrderLog,
  openManualLog,
  createManualLog,
  closeManualLog,
  openLogEdition,
  closeLogEdition,
  handleCreateManualLog,
  addManualLogTrackeableEvent,
};
